<template>
  <div>
    <v-row>
      <page-title value="年間行事予定"></page-title>
      <v-progress-circular
        v-if="loading"
        class="mx-2"
        color="primary"
        indeterminate
      ></v-progress-circular>
      <v-spacer />
      <v-btn @click="createDialog = true" color="primary" variant="flat">
        <v-icon start>mdi-plus</v-icon>
        追加
      </v-btn>
    </v-row>

    <div class="pt-10 px-12" style="height: 720px">
      <v-toolbar variant="flat" color="white">
        <v-btn
          fab
          variant="text"
          size="large"
          color="grey-darken-2"
          @click="prevMonth"
        >
          <v-icon small> mdi-chevron-left </v-icon>
        </v-btn>
        <v-btn
          fab
          variant="text"
          size="large"
          color="grey-darken-2"
          @click="nextMonth"
        >
          <v-icon small> mdi-chevron-right </v-icon>
        </v-btn>
        <v-toolbar-title v-if="calendarTitle">
          {{ calendarTitle }}
        </v-toolbar-title>
      </v-toolbar>
      <v-calendar
        v-model="model"
        :events="formattedEvents"
        @click:event="clickEvent"
        view-mode="month"
        locale="ja"
        color="primary"
      >
      </v-calendar>
    </div>

    <!-- create dialog -->
    <event-dialog-container
      dialogType="create"
      :isOpen="createDialog"
      @is-open="createDialog = $event"
    ></event-dialog-container>

    <!-- update dialog -->
    <event-dialog-container
      dialogType="update"
      :isOpen="updateDialog"
      @is-open="updateDialog = $event"
    ></event-dialog-container>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import PageTitle from "@/components/atoms/PageTitle.vue";
import { eventModule } from "@/store/dataModules/eventModule";
import { classModule } from "@/store/dataModules/classModule";
import LocalDataService from "@/service/LocalDataService";
import EventDialogContainer from "@/containers/EventDialogContainer.vue";
import { YearlyEventResponseForNursery } from "chaild-api/lib";
import { eventEditModule } from "@/store/dataModules/eventEditModule";
import dayjs from "dayjs";

interface CalendarType {
  name: string;
  start: string;
  end?: string;
  timed: boolean;
}

const now = dayjs().toDate()

export default defineComponent({
  name: "Calendar",

  components: {
    PageTitle,
    EventDialogContainer,
  },

  mounted() {
    const calendarDate = sessionStorage.getItem('calendarDate');
    if (calendarDate) {
      console.log(`calendarDate: ${calendarDate}`)
      this.model = calendarDate;
    } else {
      console.log(`this.model = now: ${now}`)
      this.model = now
    }
    this.listEvents();
    this.listClass();
  },

  watch: {
    createDialog: {
      handler(val: boolean) {
        if (!val) {
          this.listEvents();
          eventEditModule.clearValues();
        }
      },
    },
    updateDialog: {
      handler(val: boolean) {
        if (!val) {
          this.listEvents();
          eventEditModule.clearValues();
        }
      },
    },
  },

  data() {
    return {
      createDialog: false,
      updateDialog: false,
      loading: false,
      model: [dayjs().toDate()] as any,
    };
  },

  computed: {
    calendarTitle(): string {
      const targetDate = this.model;
      return dayjs(targetDate).format("YYYY年 M月");
    },
    events: function (): YearlyEventResponseForNursery[] {
      return eventModule.events;
    },
    formattedEvents: function (): CalendarType[] {
      const list = eventModule.events.map((e) => {
        return {
          name: e.eventName,
          start: e.fromTime
            ? e.date + " " + this.getFormattedTime(e.fromTime)
            : e.date,
          end: e.toTime
            ? e.date + " " + this.getFormattedTime(e.toTime)
            : e.date,
          timed: e.toTime ? true : false,
          event: e, // objectまるごと
        };
      });
      return list;
    },
  },

  methods: {
    // 前の月へ移動する処理
    prevMonth() {
      // 1. 現在のデータを安全に取り出す（Vuetifyの仕様で配列になっている場合への対策）
      let target = this.model;
      //if (Array.isArray(target)) target = target[0];
      
      // 2. dayjsを使って「1ヶ月前」を正確に計算する
      const current = target ? dayjs(target) : dayjs();
      const prevDate = current.subtract(1, 'month').toDate();
      
      // 3. 元のデータ型（配列なら配列のまま）に合わせて書き換える
      //this.model = Array.isArray(this.model) ? [prevDate.toString()] : prevDate.toString();
      this.model = prevDate;
      this.listEvents();
    },

    // 次の月へ移動する処理
    nextMonth() {
      let target = this.model;
      if (Array.isArray(target)) target = target[0];

      const current = target ? dayjs(target) : dayjs();
      const nextDate = current.add(1, 'month').toDate();

      //this.model = Array.isArray(this.model) ? [nextDate] : nextDate;
      this.model = nextDate;
      this.listEvents();
    },
    async listEvents() {
      this.loading = true;
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        let ob = {};
        if (this.model) {
          sessionStorage.setItem('calendarDate', this.model);
          const toDate = dayjs(this.model).endOf("month").format("YYYY-MM-DD");
          const fromDate = dayjs(this.model)
            .startOf("month")
            .format("YYYY-MM-DD");
          ob = {
            from: fromDate,
            to: toDate,
          };
        }
        const response = await eventModule.listEvents({
          nurseryId: nurseryId,
          ...ob,
        });
      }
      this.loading = false;
    },
    async listClass() {
      const nurseryId = LocalDataService.getNurseryId();
      if (nurseryId) {
        await classModule.listClass({
          nurseryId: nurseryId,
          // year: dayjs().year(),
        });
      }
    },
    getFormattedTime(time?: string): string {
      if (!time) {
        return "";
      }
      return time.slice(0, 2) + ":" + time.slice(2);
    },
    // 引数を ...args にして、Vuetifyが投げてくる全てのデータを捕獲する
    clickEvent(...args: any[]) {
      let target: YearlyEventResponseForNursery | null = null;

      // 捕獲した引数（PointerEventやオブジェクト等）を1つずつチェックする
      for (const arg of args) {
        if (!arg || typeof arg !== 'object') continue;

        // パターンA: そのまま行事データが入っている場合
        if (arg.yearlyEventId) {
          target = arg;
          break;
        }
        // パターンB: { event: 実際のデータ } のように包まれている場合
        if (arg.event && arg.event.yearlyEventId) {
          target = arg.event;
          break;
        }
        if (arg.event && arg.event.event && arg.event.event.yearlyEventId) {
          target = arg.event.event;
          break;
        }
        // パターンC: 【Vuetify 3 特有】 '.raw' (元データ) の中に隠されている場合
        if (arg.raw && arg.raw.event && arg.raw.event.yearlyEventId) {
          target = arg.raw.event;
          break;
        }
        // パターンD: VDataTable等と同じく { item: { raw: ... } } の構造の場合
        if (arg.item && arg.item.raw && arg.item.raw.event && arg.item.raw.event.yearlyEventId) {
          target = arg.item.raw.event;
          break;
        }
      }

      // 全力で探しても見つからなかった場合のエラーハンドリング
      if (!target) {
        console.warn("引数からイベントデータを見つけられませんでした:", args);
        return;
      }

      // 見つけた target（実際のデータ）を使って Store にセット
      eventEditModule.setEventId(target.yearlyEventId.toString());
      eventEditModule.setEventName(target.eventName);
      eventEditModule.setClassIds(target.classIds as number[]);
      eventEditModule.setToDate("");
      eventEditModule.setFromDate(target.date);
      eventEditModule.setToTime(target.toTime as string);
      eventEditModule.setFromTime(target.fromTime as string);
      
      this.updateDialog = true;
    },
  },
});
</script>
